/**
 * Native (Capacitor) bridge for the softphone.
 *
 * In the native Android app the SIP registration + calls run in NATIVE code
 * (linphone in a foreground service) so they survive swipe/lock/doze — the WebView
 * is only the dialer UI. In a plain browser this bridge no-ops and the web app
 * uses its own SIP.js (unchanged). softphone.ts branches on isNative().
 *
 * The native plugin is window.Capacitor.Plugins.Call.
 */
export interface NativeRegisterParams {
  domain: string;      // SIP server host (e.g. phone.buildmanager.pro)
  port: number;        // 5061 (tls) / 5060 (tcp)
  username: string;    // sip username == extension
  password: string;    // sip secret
  realm: string;       // t-<slug>.<domain>
  transport: 'tls' | 'tcp';
}

interface CallPlugin {
  register(p: NativeRegisterParams): Promise<void>;
  call(o: { uri: string }): Promise<void>;
  answer(): Promise<void>;
  decline(): Promise<void>;
  hangup(): Promise<void>;
  mute(o: { on: boolean }): Promise<void>;
  speaker(o: { on: boolean }): Promise<void>;
  dtmf(o: { tone: string }): Promise<void>;
  stop(): Promise<void>;
  addListener(event: string, cb: (data: any) => void): any;
}

function cap(): any | null {
  const c = (window as any).Capacitor;
  return c && c.isNativePlatform && c.isNativePlatform() ? c : null;
}
function plugin(): CallPlugin | null {
  return cap()?.Plugins?.Call ?? null;
}

/** True when running inside the native Android wrapper (with the Call plugin). */
export function isNative(): boolean {
  return !!plugin();
}

// ─── native SIP control (web → native) ───────────────────────────────────────
export async function nativeRegister(p: NativeRegisterParams): Promise<void> {
  await plugin()?.register(p);
}
export async function nativeStop(): Promise<void> { try { await plugin()?.stop(); } catch { /* */ } }
export async function nativeCall(uri: string): Promise<void> { await plugin()?.call({ uri }); }
export async function nativeAnswer(): Promise<void> { await plugin()?.answer(); }
export async function nativeDecline(): Promise<void> { await plugin()?.decline(); }
export async function nativeHangup(): Promise<void> { await plugin()?.hangup(); }
export async function nativeMute(on: boolean): Promise<void> { try { await plugin()?.mute({ on }); } catch { /* */ } }
export async function nativeSpeaker(on: boolean): Promise<void> { try { await plugin()?.speaker({ on }); } catch { /* */ } }
export async function nativeDtmf(tone: string): Promise<void> { try { await plugin()?.dtmf({ tone }); } catch { /* */ } }

// ─── native → web events ─────────────────────────────────────────────────────
export function onNativeEvent(
  cb: {
    registration?: (registered: boolean, message: string) => void;
    incomingCall?: (caller: string) => void;
    callEstablished?: () => void;
    callEnded?: () => void;
  },
): void {
  const p = plugin();
  if (!p) return;
  try {
    if (cb.registration) p.addListener('nativeRegistration', (d: any) => cb.registration!(!!d.registered, d.message ?? ''));
    if (cb.incomingCall) p.addListener('nativeIncomingCall', (d: any) => cb.incomingCall!(d.caller ?? ''));
    if (cb.callEstablished) p.addListener('nativeCallEstablished', () => cb.callEstablished!());
    if (cb.callEnded) p.addListener('nativeCallEnded', () => cb.callEnded!());
  } catch { /* */ }
}
